import { useState, useRef, useEffect, useCallback } from 'react'
import clsx from 'clsx'
import { Icon } from '@/common/components/icon'
import type { PropertyMediaResponse } from '../../api/properties'
import * as styles from './PropertyImageGallery.css'

interface PropertyImageGalleryProps {
  media: PropertyMediaResponse[]
  className?: string
}

/**
 * Media gallery component for property details page.
 * Displays photos and videos with navigation and thumbnail strip.
 */
export function PropertyImageGallery({
  media,
  className,
}: PropertyImageGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0)
  const thumbnailRefs = useRef<Map<number, HTMLButtonElement>>(new Map())
  const videoRef = useRef<HTMLVideoElement>(null)

  // Store ref for each thumbnail
  const setThumbnailRef = useCallback((index: number, el: HTMLButtonElement | null) => {
    if (el) {
      thumbnailRefs.current.set(index, el)
    } else {
      thumbnailRefs.current.delete(index)
    }
  }, [])

  // Scroll active thumbnail into view when index changes
  useEffect(() => {
    const thumbnail = thumbnailRefs.current.get(activeIndex)
    if (thumbnail) {
      thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [activeIndex])

  // Include ready and processing media, sorted by display order
  const galleryMedia = media
    .filter((m) => m.status === 'ready' || m.status === 'processing')
    .sort((a, b) => a.display_order - b.display_order)

  const hasMedia = galleryMedia.length > 0
  const activeMedia = galleryMedia[activeIndex]

  const goToPrevious = useCallback(() => {
    setActiveIndex((prev) => (prev > 0 ? prev - 1 : galleryMedia.length - 1))
  }, [galleryMedia.length])

  const goToNext = useCallback(() => {
    setActiveIndex((prev) => (prev < galleryMedia.length - 1 ? prev + 1 : 0))
  }, [galleryMedia.length])

  // Keyboard navigation handler
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (galleryMedia.length <= 1) return

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault()
          goToPrevious()
          break
        case 'ArrowRight':
          e.preventDefault()
          goToNext()
          break
        case 'Home':
          e.preventDefault()
          setActiveIndex(0)
          break
        case 'End':
          e.preventDefault()
          setActiveIndex(galleryMedia.length - 1)
          break
      }
    },
    [galleryMedia.length, goToPrevious, goToNext]
  )

  // Get thumbnail URL - use thumbnail_url for videos if available
  const getThumbnailUrl = useCallback((item: PropertyMediaResponse) => {
    if (item.media_type === 'video' && item.thumbnail_url) {
      return item.thumbnail_url
    }
    return item.file_url
  }, [])

  return (
    <div
      className={clsx(styles.container, className)}
      role="region"
      aria-label="Property media gallery"
      aria-roledescription="carousel"
      tabIndex={hasMedia && galleryMedia.length > 1 ? 0 : undefined}
      onKeyDown={handleKeyDown}
    >
      {/* Main Image/Video */}
      <div className={styles.mainImageContainer}>
        {hasMedia ? (
          <>
            {activeMedia.status === 'processing' ? (
              <div className={styles.processingPlaceholder}>
                <div className={styles.processingSpinner} />
                <span className={styles.processingLabel}>
                  {activeMedia.media_type === 'video' ? 'Video' : 'Image'} is processing
                </span>
              </div>
            ) : activeMedia.media_type === 'video' ? (
              <video
                ref={videoRef}
                key={activeMedia.id}
                src={activeMedia.file_url}
                className={styles.mainVideo}
                controls
                preload="metadata"
              />
            ) : (
              <img
                src={activeMedia.file_url}
                alt={`Property photo ${activeIndex + 1}`}
                decoding="async"
                fetchPriority="high"
                className={styles.mainImage}
              />
            )}

            {/* Caption display */}
            {activeMedia.status === 'ready' && activeMedia.caption && (
              <div className={styles.captionText}>{activeMedia.caption}</div>
            )}

            {/* Navigation buttons (only show if multiple items) */}
            {galleryMedia.length > 1 && (
              <>
                <button
                  type="button"
                  className={clsx(styles.navButton, styles.navButtonLeft)}
                  onClick={goToPrevious}
                  aria-label="Previous media"
                >
                  <Icon name="chevron_left" size={24} className={styles.navButtonIcon} />
                </button>
                <button
                  type="button"
                  className={clsx(styles.navButton, styles.navButtonRight)}
                  onClick={goToNext}
                  aria-label="Next media"
                >
                  <Icon name="chevron_right" size={24} className={styles.navButtonIcon} />
                </button>

                {/* Media counter */}
                <div className={styles.imageCounter}>
                  {activeIndex + 1} / {galleryMedia.length}
                </div>
              </>
            )}
          </>
        ) : (
          <div className={styles.placeholderImage}>
            <Icon name="home" size={48} />
          </div>
        )}
      </div>

      {/* Thumbnail strip (only show if multiple items) */}
      {galleryMedia.length > 1 && (
        <div className={styles.thumbnailStrip}>
          {galleryMedia.map((item, index) => (
            <button
              key={item.id}
              ref={(el) => setThumbnailRef(index, el)}
              type="button"
              className={clsx(
                styles.thumbnail,
                index === activeIndex && styles.thumbnailActive
              )}
              onClick={() => setActiveIndex(index)}
              aria-label={`View ${item.media_type === 'video' ? 'video' : 'photo'} ${index + 1}`}
            >
              {item.status === 'processing' ? (
                <div className={styles.processingPlaceholder}>
                  <div className={styles.thumbnailSpinner} />
                </div>
              ) : (
                <>
                  <img
                    src={getThumbnailUrl(item)}
                    alt={`Thumbnail ${index + 1}`}
                    loading="lazy"
                    decoding="async"
                    fetchPriority="low"
                    className={styles.thumbnailImage}
                  />
                  {/* Video indicator overlay */}
                  {item.media_type === 'video' && (
                    <div className={styles.thumbnailVideoIndicator}>
                      <Icon name="play_arrow" size={16} />
                    </div>
                  )}
                </>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
